import { BankAccountStatistics } from '../../../workers/BankAccountStatistics';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { DateTime } from 'luxon';
import { Pie, PieChart, ResponsiveContainer } from 'recharts';
import { CATEGORIES_PER_KEYS } from '../../../types/Categories';
import Dinero from 'dinero.js';

export interface StatsModalContentProps {
  statistics: BankAccountStatistics;
}

const prepareStats = (stats: {
  [categoryKey: string]: { amount: Dinero.DineroObject };
}) => {
  return Object.keys(stats).map((key) => ({
    category: key,
    amountValue: Dinero(stats[key].amount).toUnit(),
    amount: stats[key].amount,
    fill: CATEGORIES_PER_KEYS[key].color,
  }));
};

export const StatsModalContent = (props: StatsModalContentProps) => {
  const { statistics } = props;

  const theme = useTheme();
  const textColor = theme.palette.getContrastText(
    theme.palette.background.paper,
  );

  const [month, setMonth] = useState<{ month: number; year: number }>(
    statistics.months[statistics.months.length - 1],
  );

  const selectedMonthStats = statistics.months.find(
    (monthStats) =>
      monthStats.month === month.month && monthStats.year === month.year,
  );

  const flattenExpenses = selectedMonthStats
    ? prepareStats(selectedMonthStats.perCategoriesExpenses)
    : [];

  const flattenRevenues = selectedMonthStats
    ? prepareStats(selectedMonthStats.perCategoriesRevenues)
    : [];

  const renderCustomizedLabel = useCallback(
    (pieData: any) => {
      const amount: Dinero.DineroObject = pieData.amount;

      return (
        <text
          x={pieData.x}
          y={pieData.y - 2}
          fill={textColor}
          dominantBaseline={pieData.y > pieData.cy ? 'hanging' : 'auto'}
          textAnchor={pieData.x > pieData.cx ? 'start' : 'end'}
        >
          <tspan>({Dinero(amount).toFormat()}) </tspan>
          <tspan style={{ fontWeight: 'bold' }}>
            {CATEGORIES_PER_KEYS[pieData.category].name}
          </tspan>
        </text>
      );
    },
    [textColor],
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <FormControl variant="filled">
        <InputLabel>Mois</InputLabel>
        <Select
          variant="standard"
          labelId="demo-simple-select-filled-label"
          id="demo-simple-select-filled"
          value={month.month + '/' + month.year}
          onChange={(event) => {
            const newValue = event.target.value as string;
            const splitted = newValue.split('/');
            setMonth({ month: +splitted[0], year: +splitted[1] });
          }}
        >
          {statistics.months.map((statMonth) => (
            <MenuItem
              key={statMonth.month + '/' + statMonth.year}
              value={statMonth.month + '/' + statMonth.year}
            >
              {DateTime.fromFormat(
                statMonth.month + '/' + statMonth.year,
                'M/yyyy',
              ).toFormat('LLLL yyyy')}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box
        sx={{
          flex: 1,
          padding: 2,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
        }}
      >
        <ResponsiveContainer width={'50%'} height="100%">
          <PieChart>
            <Pie
              data={flattenExpenses}
              dataKey="amountValue"
              nameKey="category"
              label={renderCustomizedLabel}
              cx="50%"
              cy="50%"
              innerRadius={'30%'}
              outerRadius={'45%'}
              stroke={theme.palette.background.paper}
            />
            <text
              style={{ fontWeight: 'bold', fontSize: 24 }}
              x={'50%'}
              y={'50%'}
              fill={textColor}
              textAnchor={'middle'}
              dominantBaseline={'middle'}
            >
              Dépenses
            </text>
          </PieChart>
        </ResponsiveContainer>
        <ResponsiveContainer width={'50%'} height="100%">
          <PieChart>
            <Pie
              data={flattenRevenues}
              dataKey="amountValue"
              nameKey="category"
              label={renderCustomizedLabel}
              cx="50%"
              cy="50%"
              innerRadius={'30%'}
              outerRadius={'45%'}
              stroke={theme.palette.background.paper}
            />
            <text
              style={{ fontWeight: 'bold', fontSize: 24 }}
              x={'50%'}
              y={'50%'}
              fill={textColor}
              textAnchor={'middle'}
              dominantBaseline={'middle'}
            >
              Revenus
            </text>
          </PieChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
